import { ChangeEvent, useCallback } from "react";
import { BaseProjectProps } from "@custom-types/project-types";
import { useOnCheckFiles } from "@hooks/data-management/use-on-check-any-files";
import { ALLOWED_EXTENSIONS_ALL } from "@pages/data-management/import-data/import-data-utils";
import { AnyFilesInfo, UploadedIdsMap } from "@pages/data-management/data-management-types";

/** Allow selecting multiple folders. Not implemented yet in most browsers. */
const shouldAllowMultiUpload = false;

export interface FolderInputProps extends BaseProjectProps {
  /** A map of the externalIds of the uploaded ELS scans, as returned by getUploadedIdsMap. */
  uploadedIdsMap: UploadedIdsMap,
  /** Callback for setting the file infos. */
  setFileInfos: React.Dispatch<React.SetStateAction<AnyFilesInfo[]>>,
  /** Callback for setting whether the file explorer is open. */
  setIsFileExplorerOpen: React.Dispatch<React.SetStateAction<boolean>>,
  /** Flag whether file info is currently getting collected and the dropzone should show a spinner. */
  isDropzoneLoading: boolean,
  /** Callback for setting whether file info is getting collected. */
  setIsDropzoneLoading: React.Dispatch<React.SetStateAction<boolean>>,
  /** Callback for setting the file explorer input element. */
  setFileInput: React.Dispatch<React.SetStateAction<HTMLInputElement | null>>,
}

/** Hidden input element for the file explorer. */
export function FolderInput({
  project,
  uploadedIdsMap,
  setFileInfos,
  setIsFileExplorerOpen,
  isDropzoneLoading,
  setIsDropzoneLoading,
  setFileInput,
}: FolderInputProps): JSX.Element {
  const onCheckFiles = useOnCheckFiles(project);

  const allowedExtensions = ALLOWED_EXTENSIONS_ALL;
  const allowedExtensionsStr = allowedExtensions.map((extension) => `.${extension}`).join(", ");

  /**
   * Validates the selected files and adds the collected file infos.
   * Keep in sync with same function in DataManagementDropzone.
   */
  const checkFiles = useCallback(
    async (
      files: FileList | File[],
      uploadedIdsMap: UploadedIdsMap
    ): Promise<void> => {
      const fileInfosNew = await onCheckFiles(files, uploadedIdsMap);
      if (fileInfosNew.length > 0) {
        setFileInfos((prev) => [...prev, ...fileInfosNew]);
      }
    },
    [onCheckFiles, setFileInfos]
  );

  /** Selects files from the file explorer. */
  async function selectFileFromDialog(event: ChangeEvent<HTMLInputElement>): Promise<void> {
    try {
      // Show loading spinner while collecting file info.
      setIsDropzoneLoading(true);
      setIsFileExplorerOpen(false);

      // When a folder was selected, we get the individual files instead of a FileSystemDirectoryHandle/Entry.
      // But we could still recover the folder structure using files[*].webkitRelativePath.
      const files: File[] | FileList = event.target.files ?? [];
      await checkFiles(files, uploadedIdsMap);
    } finally {
      setIsDropzoneLoading(false);
    }
  }

  return (
    <input
      // We need to make sure that the next time the file input dialog is opened, it doesn't still have the file list
      // from the previous time. Setting the value attribute to the empty string is the easiest way according to
      // https://stackoverflow.com/a/54599692
      value=""
      ref={setFileInput}
      type="file"
      style={{ display: "none" }}
      accept={allowedExtensionsStr}
      onChange={(event) => void selectFileFromDialog(event)}
      multiple={shouldAllowMultiUpload}
      data-testid="sa-dropzone-input"
      disabled={isDropzoneLoading}
    />
  );
}
